import { useState, type ReactNode } from "react";
import { trpc } from "../../lib/trpc";
import { parseTrNumber } from "../../lib/trNumber";
import { Icon } from "../ui/Icon";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import type { ToastItem } from "../ui/Toast";
import "./PositionPayCard.css";

/**
 * POZİSYONUN ÜCRETLENDİRMESİ — Pozisyon çekmecesine gömülü, SALT SÜPER-ADMİN.
 *
 * İki eksen aynı kartta: **aylık brüt maaş** ve **fazla mesai saat ücreti**. İkisi de
 * tarih versiyonludur ve ikisinde de öncelik AYNIDIR:
 *
 *     kişiye özel kayıt  >  pozisyonun değeri  >  (yok)
 *
 * Yani buradan yapılan değişiklik, personel kartında KİŞİYE ÖZEL girilmiş maaşı/oranı
 * EZMEZ. Kart bunu hem metinle söyler hem de kaydetmeden ÖNCE kaç kişinin etkileneceğini
 * (ve kimlerin kendi maaşıyla kalacağını) `affected` ucundan okuyup gösterir.
 *
 * ── GÜVENLİK ──
 * Pozisyon formu `manage:hr` ile açılıyor; PARA ise süper-admin rejiminde. Bu yüzden bu
 * kart form verisinden BESLENMEZ: `position.get` para döndürmez, veriler ayrı uçlardan
 * (`hr.positionSalary`, `hr.overtimeRate`) gelir ve her uç sunucuda `assertSalaryAccess`
 * ile korunur. Buradaki `isSuper` gizlemesi bir GÖRÜNÜM kolaylığıdır, güvenlik kapısı değil.
 */

type CurrencyCode = "TRY" | "USD" | "EUR" | "GBP";
const CURRENCIES: CurrencyCode[] = ["TRY", "USD", "EUR", "GBP"];

/** Maaş: 2 ondalık. Mesai oranı: 4 (Decimal(14,4) — maaştan türetilince küsuratlı olur). */
const fmtMoney = (n: number, max: number) =>
  new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: max }).format(n);
const fmtDay = (v: string | Date | null | undefined): string => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("tr-TR");
};

interface AxisRow {
  id: string;
  amount: number;
  currency: string;
  effectiveFrom: Date | string;
  effectiveTo: Date | string | null;
  note: string | null;
}

/**
 * Bir eksenin GÖRÜNÜM kabuğu — veri bağlama dışarıda kalır.
 *
 * İki eksen (maaş / mesai) aynı bileşeni kullanır; ayrı yazılsalardı biri düzeltilip
 * diğeri unutulurdu. tRPC kancaları eksene özel olduğu için veri YUKARIDA çözülür ve
 * buraya düz değer olarak iner.
 */
function PayAxis({
  baslik, aciklama, birim, ondalik, alanOnEki,
  rows, yukleniyor, hata,
  onEkle, onSil, ekleniyor, siliniyor,
  ekstra,
}: {
  baslik: string;
  aciklama: ReactNode;
  /** Tutarın yanına basılan birim ("TRY" para biriminden sonra gelir; ör. "/sa"). */
  birim: string;
  ondalik: number;
  /** DOM id öneki — iki eksen aynı çekmecede, id'ler ÇAKIŞAMAZ. */
  alanOnEki: string;
  rows: AxisRow[];
  yukleniyor: boolean;
  hata: string | null;
  onEkle: (v: { amount: number; currency: CurrencyCode; effectiveFrom: string; note: string | null }) => void;
  onSil: (id: string) => void;
  ekleniyor: boolean;
  siliniyor: boolean;
  /** Eksene özel ek içerik (maaşta "kimler etkilenecek" önizlemesi). */
  ekstra?: ReactNode;
}) {
  const [acik, setAcik] = useState(false);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState<CurrencyCode>("TRY");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [note, setNote] = useState("");
  const [delId, setDelId] = useState<string | null>(null);

  const bugun = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00Z");
  /** BUGÜN yürürlükte olan kayıt; yoksa en yakın ileri tarihli olan (kullanıcı yazdığını görebilsin). */
  const gecerli = rows.find((r) => {
    const f = new Date(r.effectiveFrom);
    const t = r.effectiveTo ? new Date(r.effectiveTo) : null;
    return f <= bugun && (t === null || t > bugun);
  }) ?? rows.find((r) => new Date(r.effectiveFrom) > bugun) ?? null;
  const ileriTarihli = !!gecerli && new Date(gecerli.effectiveFrom) > bugun;

  const tutar = parseTrNumber(amount);
  const yazilabilir = amount.trim().length > 0 && Number.isFinite(tutar) && tutar > 0 && !!effectiveFrom && !ekleniyor;

  return (
    <div className="ppay-axis">
      <div className="ppay-row">
        <div className="ppay-label">
          <span className="ppay-title">{baslik}</span>
          <span className="ppay-help">{aciklama}</span>
        </div>
        <div className="ppay-value">
          {yukleniyor ? (
            <span className="ppay-dim">…</span>
          ) : hata ? (
            <span className="ppay-err">{hata}</span>
          ) : !gecerli ? (
            <span className="ppay-dim">tanımlı değil</span>
          ) : (
            <>
              <b className="mono">{fmtMoney(gecerli.amount, ondalik)} {gecerli.currency}{birim}</b>
              <span className="ppay-since">
                {ileriTarihli ? "→ " : ""}{fmtDay(gecerli.effectiveFrom)}
                {ileriTarihli ? " itibarıyla" : "'dan beri"}
              </span>
            </>
          )}
        </div>
        <button
          type="button"
          className="btn-md secondary sm"
          aria-expanded={acik}
          onClick={() => setAcik((v) => !v)}
        >
          <Icon name={acik ? "chevronUp" : "chevronDown"} size={14} />{gecerli ? "Değiştir" : "Tanımla"}
        </button>
      </div>

      {acik ? (
        <div className="ppay-panel">
          {ekstra}

          <div className="form-row">
            <div className="form-field">
              <label className="lbl" htmlFor={`${alanOnEki}-amount`}>Tutar <span className="req">*</span></label>
              <input
                id={`${alanOnEki}-amount`}
                className="form-input mono"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0,00"
              />
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor={`${alanOnEki}-cur`}>Para Birimi</label>
              <select
                id={`${alanOnEki}-cur`}
                className="form-select"
                value={currency}
                onChange={(e) => setCurrency(e.target.value as CurrencyCode)}
              >
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor={`${alanOnEki}-from`}>Geçerlilik Başlangıcı <span className="req">*</span></label>
              <input
                id={`${alanOnEki}-from`}
                className="form-input"
                type="date"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
            </div>
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor={`${alanOnEki}-note`}>Not</label>
            <input
              id={`${alanOnEki}-note`}
              className="form-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              placeholder="İsteğe bağlı — ör. 2026 Temmuz zammı"
            />
            <span className="help">
              Önceki değer bu tarihte kapatılır; <b>geçmiş bordrolar eski değerle kalır</b>.
            </span>
          </div>
          <button
            type="button"
            className="btn-md primary sm"
            disabled={!yazilabilir}
            onClick={() => {
              onEkle({ amount: tutar, currency, effectiveFrom, note: note.trim() || null });
              setAmount(""); setEffectiveFrom(""); setNote("");
            }}
          >
            <Icon name="check" size={14} />{ekleniyor ? "Kaydediliyor…" : "Kaydet"}
          </button>

          {rows.length > 0 ? (
            <table className="dt2 ppay-hist">
              <thead>
                <tr>
                  <th className="num" style={{ width: 150 }}>Tutar</th>
                  <th style={{ width: 110 }}>Geçerli</th>
                  <th style={{ width: 110 }}>Bitiş</th>
                  <th>Not</th>
                  <th style={{ width: 44 }} aria-label="İşlemler" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="cell-num">{fmtMoney(r.amount, ondalik)} <span className="unit">{r.currency}</span></td>
                    <td className="mono">{fmtDay(r.effectiveFrom)}</td>
                    <td className="mono">{r.effectiveTo ? fmtDay(r.effectiveTo) : <span className="ppay-dim">Açık</span>}</td>
                    <td>{r.note ?? <span className="ppay-dim">—</span>}</td>
                    <td>
                      <button
                        type="button"
                        className="iact danger"
                        title="Sil"
                        aria-label={`${baslik}: bu kaydı sil`}
                        onClick={() => setDelId(r.id)}
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}

          <ConfirmDialog
            open={!!delId}
            title={`${baslik} kaydını sil`}
            message="Bu kayıt silinecek. Onaylı/ödenmiş bir bordronun dayanağıysa işlem reddedilir."
            confirmLabel="Sil"
            tone="danger"
            busy={siliniyor}
            onCancel={() => setDelId(null)}
            onConfirm={() => { if (delId) { onSil(delId); setDelId(null); } }}
          />
        </div>
      ) : null}
    </div>
  );
}

export function PositionPayCard({
  positionId, pushToast,
}: {
  positionId: string;
  pushToast: (t: Omit<ToastItem, "id">) => void;
}) {
  const qc = trpc.useUtils();

  const salQ = trpc.hr.positionSalary.list.useQuery({ positionId }, { enabled: !!positionId });
  const rateQ = trpc.hr.overtimeRate.list.useQuery({ positionId }, { enabled: !!positionId });
  const affQ = trpc.hr.positionSalary.affected.useQuery({ positionId }, { enabled: !!positionId });

  const salAdd = trpc.hr.positionSalary.create.useMutation({
    onSuccess: () => {
      qc.hr.positionSalary.invalidate();
      pushToast({ tone: "success", message: "Pozisyon maaşı kaydedildi." });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Kaydedilemedi", message: e.message, duration: 0 }),
  });
  const salDel = trpc.hr.positionSalary.remove.useMutation({
    onSuccess: () => { qc.hr.positionSalary.invalidate(); pushToast({ tone: "success", message: "Maaş kaydı silindi." }); },
    onError: (e) => pushToast({ tone: "danger", title: "Silinemedi", message: e.message, duration: 0 }),
  });
  const rateAdd = trpc.hr.overtimeRate.create.useMutation({
    onSuccess: () => {
      qc.hr.overtimeRate.invalidate();
      pushToast({ tone: "success", message: "Mesai saat ücreti kaydedildi." });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Kaydedilemedi", message: e.message, duration: 0 }),
  });
  const rateDel = trpc.hr.overtimeRate.remove.useMutation({
    onSuccess: () => { qc.hr.overtimeRate.invalidate(); pushToast({ tone: "success", message: "Oran silindi." }); },
    onError: (e) => pushToast({ tone: "danger", title: "Silinemedi", message: e.message, duration: 0 }),
  });

  const aff = affQ.data;

  return (
    <section className="ppay">
      <div className="ppay-head">
        <span className="ppay-head-t">Ücretlendirme</span>
        <span className="badge-pill b-primary" title="Bu bölüm yalnızca sistem yöneticisine görünür ve uçlar da öyle korunur">
          <Icon name="lock" size={10} />yalnız sistem yöneticisi
        </span>
      </div>

      {/* KURALIN TAM METNİ — kullanıcının en çok sorduğu soru bu. */}
      <div className="form-banner-info ppay-note">
        <Icon name="info" size={14} />
        <span>
          Buradaki değerler bu pozisyondaki <b>tüm personel</b> için geçerlidir.
          Personel kartında <b>kişiye özel</b> maaş ya da mesai ücreti girilmişse <b>o kazanır</b> —
          burada yapılan değişiklik kişisel atamayı <b>ezmez</b>.
        </span>
      </div>

      <PayAxis
        baslik="Pozisyon Maaşı"
        aciklama="Aylık brüt"
        birim=""
        ondalik={2}
        alanOnEki="ppay-sal"
        rows={salQ.data ?? []}
        yukleniyor={salQ.isLoading}
        hata={salQ.isError ? salQ.error.message : null}
        ekleniyor={salAdd.isPending}
        siliniyor={salDel.isPending}
        onEkle={(v) => salAdd.mutate({ positionId, ...v })}
        onSil={(id) => salDel.mutate({ id })}
        ekstra={
          /* KAÇ KİŞİYİ ETKİLER — kaydetmeden ÖNCE. "40 kişiye zam yaptım" sanıp
             12'sinin kişisel maaşı olduğunu fark etmemek buradaki asıl risk. */
          affQ.isLoading ? null : !aff || aff.total === 0 ? (
            <div className="form-banner-warn ppay-note">
              <Icon name="alertTriangle" size={14} />
              Bu pozisyonda henüz personel yok — girilen maaş şimdilik kimseye uygulanmaz.
            </div>
          ) : (
            <div className="form-banner-info ppay-note">
              <Icon name="users" size={14} />
              <span>
                <b>{aff.willApply}</b> / {aff.total} personele uygulanır.
                {aff.ownSalary.length > 0 ? (
                  <> <b>{aff.ownSalary.length}</b> kişi kendi maaşıyla kalır (etkilenmez):{" "}
                    <span className="ppay-names">{aff.ownSalary.map((p) => p.name).join(", ")}</span></>
                ) : null}
              </span>
            </div>
          )
        }
      />

      <PayAxis
        baslik="Fazla Mesai Saat Ücreti"
        aciklama="Bir saatlik mesainin fiyatı — bordro katsayısı (1,5) uygulanmaz"
        birim="/sa"
        ondalik={4}
        alanOnEki="ppay-ot"
        rows={rateQ.data ?? []}
        yukleniyor={rateQ.isLoading}
        hata={rateQ.isError ? rateQ.error.message : null}
        ekleniyor={rateAdd.isPending}
        siliniyor={rateDel.isPending}
        onEkle={(v) => rateAdd.mutate({ positionId, ...v })}
        onSil={(id) => rateDel.mutate({ id })}
        ekstra={
          <div className="form-banner-info ppay-note">
            <Icon name="info" size={14} />
            Oran tanımlanmazsa mesai ücreti <b>maaştan türetilir</b> (brüt ÷ saatlik bölen × katsayı).
          </div>
        }
      />
    </section>
  );
}
